import React, { Fragment, FunctionComponent, useCallback, useState } from 'react';
import { useTranslation } from '@rbx/intl';
import { Button, Tooltip } from '@rbx/ui';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import { logTryItOutEvent } from '@modules/openCloud/swagger/components/tryItOut';
import useTryItOut from '../../hooks/useTryItOut';
import useOperation from '../../hooks/useOperation';
import TryItOutWarningDialog from './TryItOutWarningDialog';

const TryItOutButton: FunctionComponent = () => {
  const { translate } = useTranslation();
  const { tryItOutAllowed, showTryItOutWarning, tryItOutOpen, setTryItOutOpen } = useTryItOut();
  const { riskLevel, hasSupportedAuthTypes, method, url } = useOperation();

  const [warningOpen, setWarningOpen] = useState(false);

  const isDisabled = !tryItOutAllowed;

  // Determine disabled reason
  const getDisabledReason = (): string => {
    if (!hasSupportedAuthTypes) {
      return translate('Description.TryItOutAuthNotSupported');
    }

    if (!tryItOutAllowed) {
      return translate('Description.TryItOutCriticalRiskDisabled');
    }

    return '';
  };

  const toggle = useCallback(() => {
    setTryItOutOpen(!tryItOutOpen);
    logTryItOutEvent(DocsSiteEventType.TryItOutClickTryItOut, {
      isTryItOutActive: tryItOutOpen,
      method: method.toUpperCase(),
      url,
      riskLevel,
    });
  }, [tryItOutOpen, method, url, riskLevel, setTryItOutOpen]);

  const handleClick = () => {
    if (isDisabled) {
      return;
    }

    if (showTryItOutWarning) {
      setWarningOpen(true);
    } else {
      toggle();
    }
  };

  const buttonText = tryItOutOpen
    ? `✕\u00A0\u00A0${translate('Action.TryItOutClose')}`
    : translate('Action.TryItOut');

  if (isDisabled) {
    const disabledReason = getDisabledReason();

    return (
      <Tooltip title={disabledReason}>
        <span>
          <Button disabled variant='outlined'>
            {translate('Action.TryItOut')}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Fragment>
      <Button onClick={handleClick} variant='outlined' aria-label={buttonText}>
        {buttonText}
      </Button>
      <TryItOutWarningDialog
        warningOpen={warningOpen}
        setWarningOpen={setWarningOpen}
        onContinue={toggle}
      />
    </Fragment>
  );
};

export default TryItOutButton;
